/**
 * Knowledge Base · Memoria corporativa.
 * Full-text search sobre /app/memory/*.md (preparado para RAG vectorial Fase 2).
 */
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../../lib/api";
import { Books, MagnifyingGlass, FileText, X as XIcon, Sparkle, ArrowsClockwise, Code, BookOpen, DownloadSimple, Printer } from "@phosphor-icons/react";

export default function KnowledgeBase() {
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewContent, setViewContent] = useState({ content: "", loading: false });
  const [viewMode, setViewMode] = useState("rendered"); // 'rendered' | 'raw'

  const loadDocs = async () => {
    setLoadingDocs(true);
    try {
      const { data } = await api.get("/founder-os/knowledge/documents");
      setDocs(data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoadingDocs(false); }
  };

  useEffect(() => { loadDocs(); }, []);

  const search = async () => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get(`/founder-os/knowledge/search?q=${encodeURIComponent(q)}`);
      setResults(data.results || []);
    } catch (e) { console.error(e); }
    finally { setSearching(false); }
  };

  const openDoc = async (filename) => {
    setViewing(filename);
    setViewContent({ content: "", loading: true });
    try {
      const { data } = await api.get(`/founder-os/knowledge/documents/${encodeURIComponent(filename)}`);
      setViewContent({ content: data.content, loading: false });
    } catch (e) {
      setViewContent({ content: `Error: ${e?.response?.data?.detail || e.message}`, loading: false });
    }
  };

  const downloadMarkdown = () => {
    if (!viewing || !viewContent.content) return;
    const blob = new Blob([viewContent.content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = viewing;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printAsPdf = async () => {
    if (!viewing || !viewContent.content) return;
    // Render markdown to HTML via a hidden DOM node, then open a new window with full styling and trigger print
    const sourceNode = document.querySelector('[data-testid="kb-viewer-content"] .kb-prose');
    if (!sourceNode) return;
    const renderedHtml = sourceNode.innerHTML;
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=900,height=1000");
    if (!printWindow) {
      alert("Tu navegador bloqueó la ventana de impresión. Permite popups para descargar como PDF.");
      return;
    }
    const title = viewing.replace(/\.md$/i, "");
    const printedAt = new Date().toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });
    printWindow.document.write(`<!doctype html>
<html lang="es"><head><meta charset="utf-8"/>
<title>${title}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; }
  body { font-family: 'Inter', sans-serif; font-size: 11.5px; line-height: 1.6; color: #1A1A1A; background: #fff; margin: 0; padding: 40px 50px; }
  .pdf-header { border-bottom: 2px solid #FFB703; padding-bottom: 14px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: flex-end; }
  .pdf-header .brand { font-family: 'Inter Tight', sans-serif; font-weight: 700; font-size: 18px; letter-spacing: -0.02em; }
  .pdf-header .brand b { color: #FFB703; }
  .pdf-header .meta { text-align: right; font-size: 10px; color: #64748B; }
  .pdf-header .meta div { margin-bottom: 2px; }
  .pdf-body { }
  .pdf-body > * + * { margin-top: 0.9em; }
  h1 { font-family: 'Inter Tight', sans-serif; font-weight: 700; font-size: 22px; letter-spacing: -0.02em; border-bottom: 1px solid #E5E7EB; padding-bottom: 6px; margin: 0 0 12px; }
  h2 { font-family: 'Inter Tight', sans-serif; font-weight: 700; font-size: 17px; color: #B07D00; margin-top: 22px; margin-bottom: 8px; page-break-after: avoid; }
  h3 { font-family: 'Inter Tight', sans-serif; font-weight: 600; font-size: 14px; margin-top: 16px; margin-bottom: 6px; page-break-after: avoid; }
  h4, h5, h6 { font-weight: 600; margin-top: 12px; margin-bottom: 4px; }
  p { color: #334155; margin: 0; }
  strong { color: #1A1A1A; font-weight: 700; }
  em { font-style: italic; color: #1A1A1A; }
  a { color: #2563EB; text-decoration: underline; }
  code { font-family: 'JetBrains Mono', monospace; font-size: 0.85em; background: rgba(255,183,3,0.12); color: #B07D00; padding: 0.12em 0.35em; border-radius: 3px; border: 1px solid rgba(255,183,3,0.25); }
  pre { background: #F8FAFC; border: 1px solid #E5E7EB; border-radius: 6px; padding: 10px; overflow-x: auto; font-size: 10px; page-break-inside: avoid; }
  pre code { background: transparent; border: 0; padding: 0; color: #334155; }
  blockquote { border-left: 3px solid #FFB703; padding: 8px 14px; background: rgba(255,183,3,0.05); color: #475569; font-style: italic; border-radius: 0 4px 4px 0; margin: 8px 0; }
  blockquote p { margin: 4px 0; }
  ul, ol { padding-left: 22px; color: #334155; margin: 6px 0; }
  ul { list-style: disc; }
  ol { list-style: decimal; }
  li { margin-top: 2px; }
  hr { border: 0; border-top: 1px solid #E5E7EB; margin: 22px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; margin: 8px 0; page-break-inside: avoid; }
  thead { background: rgba(255,183,3,0.08); border-bottom: 2px solid #FFB703; }
  th { text-align: left; padding: 6px 8px; font-weight: 700; color: #B07D00; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; border-right: 1px solid #E5E7EB; }
  th:last-child { border-right: 0; }
  td { padding: 5px 8px; border-top: 1px solid #E5E7EB; border-right: 1px solid #E5E7EB; color: #334155; vertical-align: top; }
  td:last-child { border-right: 0; }
  input[type="checkbox"] { margin-right: 0.5em; accent-color: #FFB703; }
  del { color: #94A3B8; }
  @page { size: A4; margin: 18mm 16mm; }
  @media print {
    body { padding: 0; }
    .no-print { display: none !important; }
  }
  .print-actions { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; z-index: 9999; }
  .print-actions button { font-family: 'Inter', sans-serif; font-weight: 600; font-size: 11px; border-radius: 6px; padding: 8px 14px; cursor: pointer; border: 1px solid #E5E7EB; background: #fff; }
  .print-actions .primary { background: #FFB703; color: #1A1A1A; border-color: #FFB703; }
</style>
</head><body>
  <div class="print-actions no-print">
    <button onclick="window.close()">Cerrar</button>
    <button class="primary" onclick="window.print()">Imprimir / Guardar PDF</button>
  </div>
  <header class="pdf-header">
    <div class="brand">Auto<b>Glow</b> · Ecosistema Automotor</div>
    <div class="meta">
      <div><strong>${title}</strong></div>
      <div>Exportado: ${printedAt}</div>
    </div>
  </header>
  <div class="pdf-body">${renderedHtml}</div>
</body></html>`);
    printWindow.document.close();
    // Give the new window time to render fonts/styles before triggering print
    printWindow.focus();
    setTimeout(() => { try { printWindow.print(); } catch (e) { /* noop */ } }, 800);
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="founder-knowledge">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <Books size={11} weight="fill" /> Knowledge Base
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
            Memoria corporativa
          </h1>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            {docs.length} documentos indexados · /app/memory/*.md · preparado para RAG vectorial en Fase 2
          </p>
        </div>
        <button
          type="button"
          onClick={loadDocs}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50"
        >
          <ArrowsClockwise size={13} /> Re-indexar
        </button>
      </header>

      {/* Search */}
      <div className="relative" data-testid="kb-search-box">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-muted)]" />
        <input
          type="text"
          placeholder="Buscar en todos los documentos…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          className="w-full bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded-lg pl-10 pr-20 py-3 text-sm focus:border-[var(--ag-gold)]/50 focus:outline-none"
          data-testid="kb-search-input"
        />
        <button
          type="button"
          onClick={search}
          disabled={searching || q.trim().length < 2}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-[var(--ag-gold)] text-[#1A1A1A] text-xs font-bold px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-50"
          data-testid="kb-search-btn"
        >
          {searching ? "…" : "Buscar"}
        </button>
      </div>

      {/* Resultados de búsqueda */}
      {results.length > 0 && (
        <section data-testid="kb-results">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-3 flex items-center gap-2">
            <Sparkle size={12} weight="duotone" /> {results.length} documentos con coincidencias
          </h2>
          <div className="space-y-2">
            {results.map((r) => (
              <button
                key={r.filename}
                type="button"
                onClick={() => openDoc(r.filename)}
                className="w-full text-left p-3.5 rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] hover:border-[var(--ag-gold)]/50 transition-colors"
                data-testid={`kb-result-${r.filename}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-mono text-xs font-bold text-[var(--ag-gold)] truncate">{r.filename}</div>
                  <span className="text-[9px] text-[var(--ag-text-muted)] tabular-nums">
                    {r.match_count} coincidencias
                  </span>
                </div>
                <div className="mt-2 space-y-1.5">
                  {r.snippets.map((s, i) => (
                    <p key={i} className="text-[11px] text-[var(--ag-text-dim)] leading-relaxed line-clamp-2 font-mono">
                      {s}
                    </p>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Inventario de documentos */}
      <section>
        <h2 className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-3 flex items-center gap-2">
          <FileText size={12} weight="duotone" /> Inventario de documentos
        </h2>
        {loadingDocs ? (
          <div className="text-xs text-[var(--ag-text-dim)]">Cargando…</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2.5" data-testid="kb-docs">
            {docs.map((d) => (
              <button
                key={d.filename}
                type="button"
                onClick={() => openDoc(d.filename)}
                className="text-left p-3 rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] hover:border-[var(--ag-gold)]/50 transition-colors group"
                data-testid={`kb-doc-${d.filename}`}
              >
                <div className="font-mono text-[11px] text-[var(--ag-gold)] truncate group-hover:underline">{d.filename}</div>
                <div className="font-display text-sm font-bold mt-1 leading-tight line-clamp-1">{d.title}</div>
                {d.description && (
                  <p className="text-[10px] text-[var(--ag-text-dim)] mt-1 line-clamp-2">{d.description}</p>
                )}
                <div className="mt-2 flex items-center gap-3 text-[9px] text-[var(--ag-text-muted)] tabular-nums">
                  <span>{d.lines}L</span>
                  <span>{(d.size_bytes / 1024).toFixed(1)} KB</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Viewer */}
      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setViewing(null)}>
          <div className="bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[var(--ag-border)] flex items-center justify-between gap-3 flex-wrap">
              <div className="font-mono text-xs text-[var(--ag-gold)] truncate flex-1 min-w-[120px]">{viewing}</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={downloadMarkdown}
                  disabled={viewContent.loading || !viewContent.content}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1.5 rounded border border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-[var(--ag-text)] hover:border-[var(--ag-gold)]/40 disabled:opacity-50"
                  data-testid="kb-viewer-download-md"
                  title="Descargar como Markdown (.md)"
                >
                  <DownloadSimple size={12} weight="bold" /> .md
                </button>
                <button
                  type="button"
                  onClick={printAsPdf}
                  disabled={viewContent.loading || !viewContent.content}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1.5 rounded border border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-[var(--ag-text)] hover:border-[var(--ag-gold)]/40 disabled:opacity-50"
                  data-testid="kb-viewer-print-pdf"
                  title="Abrir vista de impresión (Guardar como PDF)"
                >
                  <Printer size={12} weight="bold" /> PDF
                </button>
              </div>
              <div className="flex items-center gap-1 bg-[var(--ag-bg)] border border-[var(--ag-border)] rounded p-0.5" data-testid="kb-viewer-mode-switch">
                <button
                  type="button"
                  onClick={() => setViewMode("rendered")}
                  className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded flex items-center gap-1 ${viewMode === "rendered" ? "bg-[var(--ag-gold)] text-[#1A1A1A]" : "text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"}`}
                  data-testid="kb-viewer-rendered"
                >
                  <BookOpen size={11} weight="bold" /> Lectura
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("raw")}
                  className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded flex items-center gap-1 ${viewMode === "raw" ? "bg-[var(--ag-gold)] text-[#1A1A1A]" : "text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"}`}
                  data-testid="kb-viewer-raw"
                >
                  <Code size={11} weight="bold" /> Markdown
                </button>
              </div>
              <button type="button" onClick={() => setViewing(null)} className="text-[var(--ag-text-dim)] p-1.5 hover:text-[var(--ag-text)]" data-testid="kb-viewer-close">
                <XIcon size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="kb-viewer-content">
              {viewContent.loading ? (
                <div className="p-5 text-xs text-[var(--ag-text-dim)]">Cargando…</div>
              ) : viewMode === "rendered" ? (
                <div className="p-6 md:p-8 kb-prose">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{viewContent.content}</ReactMarkdown>
                </div>
              ) : (
                <pre className="p-5 font-mono text-xs leading-relaxed whitespace-pre-wrap text-[var(--ag-text-dim)]">
                  {viewContent.content}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="text-[10px] text-[var(--ag-text-muted)] text-center pt-4">
        Búsqueda actual: full-text con highlighting · próxima fase: embeddings + RAG vectorial
      </p>
    </div>
  );
}
